import { cn } from '../../utils/cn';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  render?: (item: T, index: number) => React.ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (item: T) => string;
  isLoading?: boolean;
  emptyMessage?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
  onRowClick?: (item: T) => void;
  selectedRows?: string[];
  onSelectRow?: (key: string) => void;
  onSelectAll?: () => void;
  showCheckbox?: boolean;
  compact?: boolean;
}

export function Table<T extends object>({
  columns,
  data,
  keyExtractor,
  isLoading = false,
  emptyMessage = 'No data available',
  sortColumn,
  sortDirection,
  onSort,
  onRowClick,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  showCheckbox = false,
  compact = false,
}: TableProps<T>) {
  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected =
    selectedRows.length > 0 && selectedRows.length < data.length;

  return (
    <div className="w-full max-w-full [contain:paint] overflow-x-auto rounded-2xl border border-gray-200 dark:border-[var(--amy-border)]">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-[var(--amy-border)]">
        <thead className="bg-gray-50 dark:bg-[var(--amy-bg)]">
          <tr>
            {showCheckbox && (
              <th className="w-12 px-4 py-3" aria-label="Select rows">
                <span className="sr-only">Select rows</span>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  aria-label="Select all rows"
                  className="h-4 w-4 rounded border-gray-300 accent-[var(--amy-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--amy-accent)] dark:border-[var(--amy-border)] dark:bg-[var(--amy-field)]"
                />
              </th>
            )}
            {columns.map(column => (
              <th
                key={column.key}
                aria-label={
                  column.header
                    ? undefined
                    : column.key === 'actions'
                      ? 'Actions'
                      : column.key
                }
                aria-sort={
                  column.sortable && sortColumn === column.key
                    ? sortDirection === 'asc'
                      ? 'ascending'
                      : sortDirection === 'desc'
                        ? 'descending'
                        : 'none'
                    : undefined
                }
                className={cn(
                  'text-left text-xs font-medium text-gray-500 dark:text-[var(--amy-muted)] uppercase tracking-wider',
                  compact ? 'px-2 py-2' : 'px-4 py-3',
                  column.width
                )}
              >
                {column.sortable ? (
                  <button
                    type="button"
                    onClick={() => onSort?.(column.key)}
                    className="-m-1 flex items-center gap-1 rounded p-1 text-left uppercase hover:text-[var(--amy-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--amy-accent)]"
                  >
                    {column.header}
                    <SortIcon
                      isActive={sortColumn === column.key}
                      direction={
                        sortColumn === column.key ? sortDirection : undefined
                      }
                    />
                  </button>
                ) : (
                  column.header || (
                    <span className="sr-only">
                      {column.key === 'actions' ? 'Actions' : column.key}
                    </span>
                  )
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-[var(--amy-card)] divide-y divide-gray-200 dark:divide-[var(--amy-border)]">
          {isLoading ? (
            <tr>
              <td
                colSpan={columns.length + (showCheckbox ? 1 : 0)}
                className="px-4 py-12 text-center"
              >
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--amy-accent)]"></div>
                  <span className="ml-2 text-gray-500 dark:text-[var(--amy-muted)]">
                    Loading...
                  </span>
                </div>
              </td>
            </tr>
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (showCheckbox ? 1 : 0)}
                className="px-4 py-12 text-center text-gray-500 dark:text-[var(--amy-muted)]"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item, index) => {
              const key = keyExtractor(item);
              const isSelected = selectedRows.includes(key);

              return (
                <tr
                  key={key}
                  className={cn(
                    'transition-colors',
                    onRowClick &&
                      'cursor-pointer hover:bg-gray-50 dark:hover:bg-[var(--amy-field)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--amy-accent)]',
                    isSelected &&
                      'bg-[var(--amy-accent)]/10 dark:bg-[var(--amy-accent)]/10'
                  )}
                  onClick={event => {
                    if (
                      event.target instanceof HTMLElement &&
                      event.target.closest('a, button, input, select, textarea')
                    )
                      return;
                    onRowClick?.(item);
                  }}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? event => {
                          if (event.target !== event.currentTarget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onRowClick(item);
                          }
                        }
                      : undefined
                  }
                >
                  {showCheckbox && (
                    <td
                      className="w-12 px-4 py-3"
                      onClick={e => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select row ${key}`}
                        checked={isSelected}
                        onChange={() => onSelectRow?.(key)}
                        className="h-4 w-4 rounded border-gray-300 accent-[var(--amy-accent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--amy-accent)] dark:border-[var(--amy-border)] dark:bg-[var(--amy-field)]"
                      />
                    </td>
                  )}
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className={cn(
                        'text-sm text-gray-900 dark:text-[var(--amy-text)]',
                        compact ? 'px-2 py-2' : 'px-4 py-3'
                      )}
                    >
                      {column.render
                        ? column.render(item, index)
                        : ((item as Record<string, unknown>)[
                            column.key
                          ] as React.ReactNode)}
                    </td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function SortIcon({
  isActive,
  direction,
}: {
  isActive: boolean;
  direction?: 'asc' | 'desc';
}) {
  if (!isActive) {
    return (
      <ChevronsUpDown className="w-4 h-4 text-gray-400 dark:text-[var(--amy-muted)]" />
    );
  }
  return direction === 'asc' ? (
    <ChevronUp className="w-4 h-4 text-[var(--amy-accent)]" />
  ) : (
    <ChevronDown className="w-4 h-4 text-[var(--amy-accent)]" />
  );
}

// Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}: PaginationProps) {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-[var(--amy-card)] border-t border-gray-200 dark:border-[var(--amy-border)]">
      <div className="text-sm text-gray-500 dark:text-[var(--amy-muted)]">
        {totalItems === 0 ? (
          'No results'
        ) : (
          <>
            Showing <span className="font-medium">{startItem}</span> to{' '}
            <span className="font-medium">{endItem}</span> of{' '}
            <span className="font-medium">{totalItems}</span> results
          </>
        )}
      </div>
      <div className="flex gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={totalItems === 0 || currentPage <= 1}
          className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[var(--amy-card)] dark:border-[var(--amy-border)] dark:text-[var(--amy-text)] dark:hover:bg-[var(--amy-field)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--amy-accent)]"
        >
          Previous
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let page: number;
          if (totalPages <= 5) {
            page = i + 1;
          } else if (currentPage <= 3) {
            page = i + 1;
          } else if (currentPage >= totalPages - 2) {
            page = totalPages - 4 + i;
          } else {
            page = currentPage - 2 + i;
          }
          return (
            <button
              key={page}
              type="button"
              aria-current={currentPage === page ? 'page' : undefined}
              onClick={() => onPageChange(page)}
              className={cn(
                'px-3 py-1 text-sm font-medium rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--amy-accent)]',
                currentPage === page
                  ? 'bg-[var(--amy-accent)] text-[var(--amy-on-accent)]'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-[var(--amy-card)] dark:border-[var(--amy-border)] dark:text-[var(--amy-text)] dark:hover:bg-[var(--amy-field)]'
              )}
            >
              {page}
            </button>
          );
        })}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={totalItems === 0 || currentPage >= totalPages}
          className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-[var(--amy-card)] dark:border-[var(--amy-border)] dark:text-[var(--amy-text)] dark:hover:bg-[var(--amy-field)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--amy-accent)]"
        >
          Next
        </button>
      </div>
    </div>
  );
}
